import cn from 'classnames';
import { MenuItem } from 'primereact/menuitem';
import { Steps } from 'primereact/steps';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';

import { useBuilderCampaignData } from '@features/campaigns/builder/providers/BuilderCampaignDataProvider/useBuilderCampaignData.ts';
import { useBuilderStepNavigation } from '@features/campaigns/builder/providers/BuilderStepNavigationProvider/useBuilderStepNavigation.ts';
import { allowAccessToStep } from '@features/campaigns/builder/utils/allowAccessToStep.ts';
import { getInvalidSectionNames } from '@features/campaigns/builder/utils/getInvalidSectionNames';
import { i18nNameSpace } from '@shared/consts/i18n.ts';

import styles from './Stepper.module.scss';

interface StepperProps {
  activeIndex: number;
}

function Stepper({ activeIndex }: StepperProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { requestNavigationToStep } = useBuilderStepNavigation();
  const { campaignData } = useBuilderCampaignData();

  const { campaignId, invalidSections } = campaignData;
  const invalidSectionNames = getInvalidSectionNames(invalidSections);

  const buildItems = (labels: string[]): MenuItem[] => {
    return labels.map((label, index) => {
      const currentStepNumber = index + 1;
      const isStepDisabled = !allowAccessToStep(index, campaignId, invalidSectionNames);
      const disabledStepTooltipText = isStepDisabled ? t('builder.steps.disabledStepNavigationItem') : '';

      const handleClick = (event: React.MouseEvent) => {
        event.preventDefault();

        if (!isStepDisabled) {
          requestNavigationToStep(index);
        }
      };

      return {
        template: (
          <a
            href='#'
            className={cn('p-menuitem-link navigation-step-tooltip', {
              'p-disabled': isStepDisabled,
            })}
            role='presentation'
            aria-label={label}
            aria-current={activeIndex === index}
            aria-disabled={isStepDisabled}
            tabIndex={isStepDisabled ? -1 : 0}
            data-pc-section='action'
            data-pr-tooltip={disabledStepTooltipText}
            data-pr-position='bottom'
            data-pr-mousetrack
            onClick={handleClick}
          >
            <span className='p-steps-number' data-pc-section='step'>
              {currentStepNumber}
            </span>
            <span className='p-steps-title' data-pc-section='label'>
              {label}
            </span>
          </a>
        ),
      };
    });
  };

  const items = buildItems([
    t('builder.steps.campaignDetails'),
    t('builder.steps.target'),
    t('builder.steps.adProducts'),
    t('builder.steps.availabilityAndCosts'),
    t('builder.steps.reviewAndSubmit'),
  ]);

  return (
    <div className={styles.customStepper} data-testid='stepper'>
      <Tooltip target='.navigation-step-tooltip' />
      <Steps model={items} activeIndex={activeIndex} readOnly={false} />
    </div>
  );
}

export default Stepper;
